// Platsens inställningar (b5), bara för ägaren: platsen, elen, förvärmningen, värmemodellen
// under Avancerat, styrningen i molnet med en bekräftelse, naven och deras nycklar, ägarna och
// läsarna, och loggen över ändringarna. Varje ändring loggas i molnet med vem, när, från vad
// och till vad.
//
// b6: nättariffen och påslaget per period under El, skattefaktorn under Avancerat, och
// "ej i drift" under Drift.
//
// b8: Givare (Ruuvi), gränserna för varningen för hård vind för en båt, och golvet under Nav.
//
// b10: Vinterläge för en båt med ett nav ombord (vinter.tsx), styrningen i molnet för det, och
// gränserna för kylskåpet och frysen för alla platser.
//
// b11: när styrningen i molnet slås av för en båt med vinterläget säger frågan att laddarna
// slås på först (BK33), och kortet visar svaret från navet. Gränserna för svartvattentanken
// under Tankar, och kortet Badtemperatur av och på (BK31).
//
// b13: kortet Väderstationen med gränserna för misstänkt is, för alla platser, och sidorna och
// tankarna under Motorerna (b12, b13). b14: Båten (baten.tsx) ersätter Motorerna.
//
// b20-2: Ytterdörren, med vistelserna (ytterdorr.tsx), för ett hus.
//
// b24: Värmen vid ankomst och avresa (varmeprofil.tsx), för ett hus: som förut, eller en egen profil
// med ett läge och en eko-temperatur per zon.

import {
  datumOchTid,
  ELOMRADEN,
  type Modellparameter,
  type Nattariff,
  type Navsort,
  PERIODER,
  type Period,
  type Platsinstallningar,
  type Roll,
  sedan,
} from "@batkoll/gemensam";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar, type Navloggrad, type Platsandring } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";
import { Batdel } from "./baten.tsx";
import { Givardel } from "./givare.tsx";
import { DELA_TEXT } from "./guiden.tsx";
import { Homeydel } from "./homey.tsx";
import { Jordgivardel } from "./jordfukt.tsx";
import {
  Nyckelruta,
  Positionsval,
  type Positionsvarde,
  Tidszonsval,
  TYPVAL,
} from "./platsform.tsx";
import { Husforbrukardel } from "./utblas.tsx";
import { Varmeprofildel } from "./varmeprofil.tsx";
import { Victrondel } from "./victron.tsx";
import { Vinterdel } from "./vinter.tsx";
import { Ytterdorrdel } from "./ytterdorr.tsx";

/**
 * Sparar en del och visar beskedet under den. `gor` ger de nya inställningarna, eller dem och ett
 * besked från molnet. Sant om det gick.
 */
type Spara = (
  sektion: string,
  gor: () => Promise<Platsinstallningar | [Platsinstallningar, string]>,
  ok?: string,
) => Promise<boolean>;

interface Besked {
  sektion: string;
  text: string | null;
  fel: string | null;
}

const KALLA: Record<Modellparameter["kalla"], string> = {
  förval: "förval",
  skattad: "skattad ur historiken",
  inställd: "inställd",
};

const NAVNAMN: Record<Navsort, string> = {
  ombord: "Ombord",
  hus: "I huset (Nobø)",
  ha: "Home Assistant",
};

/** Var navet sitter, i en mening: "navet i huset (Nobø)". */
const NAVET: Record<Navsort, string> = {
  ombord: "navet ombord",
  hus: "navet i huset (Nobø)",
  ha: "navet i Home Assistant",
};

const MALNAMN: Record<string, string> = {
  nobo: "Nobø",
  ha: "Home Assistant",
  masterbus: "vinterläget",
};

/** Ett tal ur ett fält, med decimalkomma eller punkt. NaN om fältet inte är ett tal. */
const lasTal = (s: string) => (s.trim() ? Number(s.trim().replace(",", ".")) : Number.NaN);
const visaTal = (v: number) => String(v).replace(".", ",");

function Beskedrad(props: { b: Besked | null; sektion: string }) {
  const b = props.b;
  if (!b || b.sektion !== props.sektion) return null;
  if (b.fel) {
    return (
      <p class="fel" role="alert">
        {b.fel}
      </p>
    );
  }
  return b.text ? (
    <p class="ok-text" role="status">
      {b.text}
    </p>
  ) : null;
}

function Platsdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const p = props.s.plats;
  const [namn, setNamn] = useState(p.namn);
  const [typ, setTyp] = useState(p.typ);
  const [tidszon, setTidszon] = useState(p.tidszon);
  const [pos, setPos] = useState<Positionsvarde>({ position: p.position, giltig: true });
  const typer =
    p.typ === "bat" ? TYPVAL.filter((t) => t.typ === "bat") : TYPVAL.filter((t) => t.typ !== "bat");

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("plats", async () => {
      if (!pos.giltig) throw new Felsvar("Positionen är inte giltig.", 400);
      return api.andraPlats(p.id, { namn, typ, tidszon, position: pos.position });
    });
  }

  return (
    <Kort rubrik="Platsen">
      <form class="formular" onSubmit={spara}>
        <label>
          Namn
          <input
            required
            maxLength={60}
            value={namn}
            onInput={(e) => setNamn(e.currentTarget.value)}
          />
        </label>
        <label>
          Typ
          <select
            value={typ}
            disabled={p.typ === "bat"}
            onChange={(e) => setTyp(e.currentTarget.value as typeof typ)}
          >
            {typer.map((t) => (
              <option key={t.typ} value={t.typ}>
                {t.namn}
              </option>
            ))}
          </select>
        </label>
        <Tidszonsval varde={tidszon} andra={setTidszon} />
        <Positionsval start={p.position} andra={setPos} />
        <Beskedrad b={props.besked} sektion="plats" />
        <button class="knapp-huvud" type="submit">
          Spara platsen
        </button>
      </form>
    </Kort>
  );
}

/** Ett valfritt tal ur ett fält: null om det är tomt. Kastar om det inte är ett tal. */
function valfrittTal(s: string, namn: string): number | null {
  const v = s.trim() ? lasTal(s) : null;
  if (v !== null && !Number.isFinite(v)) throw new Felsvar(`${namn} ska vara ett tal.`, 400);
  return v;
}

function Eldel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const el = props.s.el;
  const [omrade, setOmrade] = useState<string>(el?.elomrade ?? "");
  const [avtal, setAvtal] = useState<string>(el?.elavtal ?? "");
  const [paslag, setPaslag] = useState(el?.paslag === null || !el ? "" : visaTal(el.paslag));
  const [tariff, setTariff] = useState<Nattariff>(el?.nattariff ?? "ingen");
  const [perioder, setPerioder] = useState<Record<Period, string>>({
    P1: el?.paslagPerioder.P1 == null ? "" : visaTal(el.paslagPerioder.P1),
    P2: el?.paslagPerioder.P2 == null ? "" : visaTal(el.paslagPerioder.P2),
    P3: el?.paslagPerioder.P3 == null ? "" : visaTal(el.paslagPerioder.P3),
  });
  if (!el) return null;
  const valt = omrade || el.forval || "";
  const enhet = valt === "ES" ? "cent/kWh" : valt ? "öre/kWh" : el.paslagsenhet;
  const pengar = valt === "ES" ? "cent" : valt.startsWith("NO") ? "øre" : "öre";
  // ES 2.0TD gäller bara elområdet ES (b6).
  const tariffer: Nattariff[] = valt === "ES" ? ["ingen", "ES 2.0TD"] : ["ingen"];
  const tariffen: Nattariff = tariffer.includes(tariff) ? tariff : "ingen";
  const perPeriod = tariffen === "ES 2.0TD" && avtal === "timpris";
  const skatt = el.skattefaktor.varde;

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("el", async () => {
      const andring: Platsandring = {
        elomrade: omrade || null,
        elavtal: avtal || null,
        nattariff: tariffen,
      };
      if (perPeriod) {
        for (const x of PERIODER) {
          andring[`paslag${x}`] = valfrittTal(perioder[x], `Påslaget i ${x}`);
        }
      } else {
        andring.paslag = valfrittTal(paslag, "Påslaget");
      }
      return api.andraPlats(props.s.plats.id, andring);
    });
  }

  return (
    <Kort rubrik="El">
      <form class="formular" onSubmit={spara}>
        <label>
          Elområde
          <select value={omrade} onChange={(e) => setOmrade(e.currentTarget.value)}>
            <option value="">Förval efter tidszonen ({el.forval ?? "inget"})</option>
            {ELOMRADEN.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Elavtal
          <select value={avtal} onChange={(e) => setAvtal(e.currentTarget.value)}>
            <option value="">Inte ifyllt</option>
            <option value="timpris">Timpris (följer spotpriset)</option>
            <option value="fast">Fast pris</option>
          </select>
        </label>
        {tariffer.length > 1 && (
          <label>
            Nättariff
            <select
              value={tariffen}
              onChange={(e) => setTariff(e.currentTarget.value as Nattariff)}
            >
              <option value="ingen">Ingen: samma påslag hela dygnet</option>
              <option value="ES 2.0TD">ES 2.0TD: påslaget beror på perioden</option>
            </select>
          </label>
        )}
        {perPeriod ? (
          <>
            {PERIODER.map((x) => (
              <label key={x}>
                Påslag {x} ({enhet})
                <input
                  inputMode="decimal"
                  autoComplete="off"
                  value={perioder[x]}
                  onInput={(e) => {
                    const v = e.currentTarget.value;
                    setPerioder((f) => ({ ...f, [x]: v }));
                  }}
                />
              </label>
            ))}
            <p class="liten">{el.periodtext}</p>
          </>
        ) : (
          <label>
            {avtal === "fast" ? "Pris" : "Påslag"} ({enhet})
            <input
              inputMode="decimal"
              autoComplete="off"
              value={paslag}
              onInput={(e) => setPaslag(e.currentTarget.value)}
            />
          </label>
        )}
        <p class="liten">
          Timpris: värmen går de billigaste kvartarna, men varmt i tid går före priset. Fast pris:
          värmen går så sent som möjligt, eftersom det kräver minst energi. Påslaget är det du
          betalar per kWh utöver spotpriset, i {pengar}: elnätets avgift, energiskatten och
          elhandlarens påslag. Med fast pris: hela priset per kWh. Ett ungefärligt värde räcker.
        </p>
        {avtal !== "fast" && (
          <p class="liten">
            {skatt !== 1
              ? `Skattefaktorn är på (${visaTal(skatt)}): spotpriset räknas med skatt och moms, och påslaget ska vara med skatt och moms.`
              : "Skattefaktorn är av: spotpriset räknas utan skatt och moms. Ta med dem i påslaget om kostnaden ska stämma med fakturan."}{" "}
            Skattefaktorn ändras under Avancerat.
          </p>
        )}
        {avtal === "timpris" && !el.priser && (
          <p class="liten varning-text">
            Molnet har inga elpriser för {valt || "elområdet"} just nu. Då börjar förvärmningen ett
            fast antal timmar före ankomsten, som förut. Med fast pris går värmen i stället så sent
            som möjligt.
          </p>
        )}
        <Beskedrad b={props.besked} sektion="el" />
        <button class="knapp-huvud" type="submit">
          Spara elen
        </button>
      </form>
    </Kort>
  );
}

function Forvarmningsdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const f = props.s.forvarmning;
  const [timmar, setTimmar] = useState(f ? visaTal(f.timmar) : "");
  if (!f) return null;
  const id = props.s.plats.id;

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("forvarmning", async () => {
      const v = lasTal(timmar);
      if (!Number.isFinite(v)) throw new Felsvar("Skriv ett antal timmar.", 400);
      return api.andraPlats(id, { forvarmningTimmar: v });
    });
  }

  return (
    <Kort rubrik="Förvärmning">
      <form class="formular" onSubmit={spara}>
        <label>
          Timmar före ankomsten
          <input
            inputMode="decimal"
            autoComplete="off"
            value={timmar}
            onInput={(e) => setTimmar(e.currentTarget.value)}
          />
        </label>
        <p class="liten">
          När förvärmningen inte kan planeras efter elpriset börjar den så här många timmar före
          ankomsten, och planen jämför med den. Förval: {visaTal(f.forval)} timmar
          {f.installd ? ". Nu inställd." : "."} Mellan {f.min} och {f.max}.
        </p>
        <Beskedrad b={props.besked} sektion="forvarmning" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara förvärmningen
          </button>
          {f.installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("forvarmning", () => api.andraPlats(id, { forvarmningTimmar: null }))
              }
            >
              Återställ till förvalet
            </button>
          )}
        </div>
      </form>
    </Kort>
  );
}

/** b8: gränserna för varningen för hård vind, för en båt. */
function Vinddel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const v = props.s.vind;
  const [kuling, setKuling] = useState(v ? visaTal(v.kulingMs.varde) : "");
  const [byar, setByar] = useState(v ? visaTal(v.byarMs.varde) : "");
  if (!v) return null;
  const id = props.s.plats.id;

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("vind", async () => {
      const k = lasTal(kuling);
      const b = lasTal(byar);
      if (!Number.isFinite(k) || !Number.isFinite(b)) {
        throw new Felsvar("Skriv medelvinden och byarna i m/s.", 400);
      }
      return api.andraPlats(id, { vind: { kulingMs: k, byarMs: b } });
    });
  }

  return (
    <Kort rubrik="Vind">
      <form class="formular" onSubmit={spara}>
        <label>
          Varning vid medelvind (m/s)
          <input
            inputMode="decimal"
            autoComplete="off"
            value={kuling}
            onInput={(e) => setKuling(e.currentTarget.value)}
          />
        </label>
        <label>
          Varning vid byar (m/s)
          <input
            inputMode="decimal"
            autoComplete="off"
            value={byar}
            onInput={(e) => setByar(e.currentTarget.value)}
          />
        </label>
        <p class="liten">
          En Varning när prognosen för båten har så hård vind inom ett dygn. Den bygger på en
          prognos, inte på båtens givare, och kommer en gång per tillfälle. Förval: medelvind{" "}
          {visaTal(v.kulingMs.forval)} m/s (kuling) och byar {visaTal(v.byarMs.forval)} m/s
          {v.kulingMs.installd || v.byarMs.installd ? ". Nu inställd." : "."}
        </p>
        <Beskedrad b={props.besked} sektion="vind" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara vinden
          </button>
          {(v.kulingMs.installd || v.byarMs.installd) && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("vind", () =>
                  api.andraPlats(id, { vind: { kulingMs: null, byarMs: null } }),
                )
              }
            >
              Återställ till förvalen
            </button>
          )}
        </div>
      </form>
    </Kort>
  );
}

/** I drift eller ej i drift (b6). */
function Driftdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const iDrift = props.s.plats.iDrift;
  const id = props.s.plats.id;
  return (
    <Kort rubrik="Drift">
      <Rad namn="Platsen">{iDrift ? "I drift" : "Ej i drift"}</Rad>
      <p class="liten">
        Ej i drift passar till exempel en båt där navet inte är monterat än, eller en plats som är
        avstängd för säsongen. Det tystar bara varningarna om att navet eller en enhet har varit
        tyst, och deras notiser. Allt som navet ändå skickar bedöms som vanligt, och Akut kommer
        alltid fram. Styrningen påverkas inte.
      </p>
      <Beskedrad b={props.besked} sektion="drift" />
      <button
        type="button"
        class="knapp-andra"
        onClick={() =>
          props.spara(
            "drift",
            () => api.andraPlats(id, { iDrift: !iDrift }),
            iDrift ? "Platsen är ej i drift." : "Platsen är i drift.",
          )
        }
      >
        {iDrift ? "Sätt som ej i drift" : "Ta i drift"}
      </button>
    </Kort>
  );
}

/** Skattefaktorn på spotpriset, under Avancerat (b6). */
function Skattefaktordel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const el = props.s.el;
  const [faktor, setFaktor] = useState(el ? visaTal(el.skattefaktor.varde) : "");
  if (!el) return null;
  const id = props.s.plats.id;
  const sf = el.skattefaktor;
  const omrade = el.elomrade ?? el.forval ?? "elområdet";

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("skattefaktor", async () => {
      const v = lasTal(faktor);
      if (!Number.isFinite(v)) throw new Felsvar("Skattefaktorn ska vara ett tal.", 400);
      return api.andraPlats(id, { skattefaktor: v });
    });
  }

  return (
    <details class="avancerat" open={sf.installd && sf.varde !== sf.forval}>
      <summary>Skattefaktorn på spotpriset</summary>
      <p class="liten">
        Med timpris räknas kostnaden som spotpriset gånger skattefaktorn, plus påslaget. Faktorn är
        elskatten och momsen på spotpriset, så att kostnaden stämmer med fakturan. 1 är av: då
        räknas spotpriset utan skatt och moms. Förval för {omrade}: {visaTal(sf.forval)}
        {sf.installd ? `. Nu inställd: ${visaTal(sf.varde)}.` : "."} Med fast pris används den inte.
      </p>
      <form class="formular" onSubmit={spara}>
        <label>
          Skattefaktor
          <input
            inputMode="decimal"
            autoComplete="off"
            value={faktor}
            onInput={(e) => setFaktor(e.currentTarget.value)}
          />
        </label>
        <Beskedrad b={props.besked} sektion="skattefaktor" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara skattefaktorn
          </button>
          {sf.installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("skattefaktor", () => api.andraPlats(id, { skattefaktor: null }))
              }
            >
              Återställ till förvalet ({visaTal(sf.forval)})
            </button>
          )}
        </div>
      </form>
    </details>
  );
}

function Avanceratdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const vm = props.s.varmemodell;
  const [varden, setVarden] = useState<Record<string, string>>(
    Object.fromEntries((vm?.parametrar ?? []).map((x) => [x.nyckel, visaTal(x.varde)])),
  );
  if (!vm) return null;
  const id = props.s.plats.id;
  const installda = vm.parametrar.some((x) => x.kalla === "inställd");

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("avancerat", async () => {
      const andring: Record<string, number | null> = {};
      for (const x of vm?.parametrar ?? []) {
        const v = lasTal(varden[x.nyckel] ?? "");
        if (!Number.isFinite(v)) throw new Felsvar(`${x.namn} ska vara ett tal.`, 400);
        // Bara det som ändrats blir inställt. Resten får fortsätta följa skattningen.
        if (Math.abs(v - x.varde) > 1e-9) andring[x.nyckel] = v;
      }
      return api.andraPlats(id, { varmemodell: andring });
    });
  }

  return (
    <Kort rubrik="Avancerat" bred>
      <details class="avancerat">
        <summary>Värmemodellen</summary>
        <p class="liten">
          Förvärmningen räknar med en modell av huset: värmens effekt, förlusten per grad mot ute,
          och energin för en grad varmare. Molnet skattar förlusten och kapaciteten ur historiken
          varje dygn, när det finns tillräckligt med mätningar. Annars gäller förvalen. Ändra bara
          om du vet bättre, till exempel summan av elementens effekt.
        </p>
        <form class="formular" onSubmit={spara}>
          {vm.parametrar.map((x) => (
            <label key={x.nyckel}>
              {x.namn}, {x.enhet} ({KALLA[x.kalla]})
              <input
                inputMode="decimal"
                autoComplete="off"
                value={varden[x.nyckel] ?? ""}
                onInput={(e) => {
                  const v = e.currentTarget.value;
                  setVarden((f) => ({ ...f, [x.nyckel]: v }));
                }}
              />
            </label>
          ))}
          <p class="liten">{vm.text}</p>
          <Beskedrad b={props.besked} sektion="avancerat" />
          <div class="knapprad">
            <button class="knapp-huvud" type="submit">
              Spara värmemodellen
            </button>
            {installda && (
              <button
                type="button"
                class="knapp-andra"
                onClick={() =>
                  props.spara("avancerat", () =>
                    api.andraPlats(id, {
                      varmemodell: {
                        effektKw: null,
                        forlustKwPerGrad: null,
                        kapacitetKwhPerGrad: null,
                      },
                    }),
                  )
                }
              >
                Återställ till förval och skattning
              </button>
            )}
          </div>
        </form>
      </details>
      <Skattefaktordel s={props.s} spara={props.spara} besked={props.besked} />
    </Kort>
  );
}

/** b10: gränserna för kylskåpet och frysen, för alla platser, som vinden. */
function KylFrysdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const g = props.s.kylFrys;
  const [kyl, setKyl] = useState(visaTal(g.kylMaxGrader.varde));
  const [kylMin, setKylMin] = useState(visaTal(g.kylMinuter.varde));
  const [frys, setFrys] = useState(visaTal(g.frysMaxGrader.varde));
  const [frysMin, setFrysMin] = useState(visaTal(g.frysMinuter.varde));
  const id = props.s.plats.id;
  const installd = [g.kylMaxGrader, g.kylMinuter, g.frysMaxGrader, g.frysMinuter].some(
    (x) => x.installd,
  );

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("kylfrys", async () => {
      const v = [kyl, kylMin, frys, frysMin].map(lasTal);
      if (!v.every(Number.isFinite)) {
        throw new Felsvar("Skriv gränserna i grader och minuter.", 400);
      }
      const [kylMaxGrader, kylMinuter, frysMaxGrader, frysMinuter] = v as number[];
      return api.andraPlats(id, {
        kylFrys: { kylMaxGrader, kylMinuter, frysMaxGrader, frysMinuter },
      });
    });
  }

  const falt = (etikett: string, varde: string, satt: (v: string) => void) => (
    <label>
      {etikett}
      <input
        inputMode="decimal"
        autoComplete="off"
        value={varde}
        onInput={(e) => satt(e.currentTarget.value)}
      />
    </label>
  );

  return (
    <Kort rubrik="Kylskåp och frys">
      <form class="formular" onSubmit={spara}>
        {falt("Varning när kylskåpet är varmare än (°C)", kyl, setKyl)}
        {falt("Kylskåpet: i så här många minuter", kylMin, setKylMin)}
        {falt("Varning när frysen är varmare än (°C)", frys, setFrys)}
        {falt("Frysen: i så här många minuter", frysMin, setFrysMin)}
        <p class="liten">
          En Varning en gång per tillfälle, och bara när platsen är i drift. Den kräver en givare i
          kylskåpet eller frysen (Givare). Förval: kylskåpet {visaTal(g.kylMaxGrader.forval)} °C och
          frysen {visaTal(g.frysMaxGrader.forval)} °C, i {visaTal(g.kylMinuter.forval)} minuter
          {installd ? ". Nu inställd." : "."}
        </p>
        <Beskedrad b={props.besked} sektion="kylfrys" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara kylskåpet och frysen
          </button>
          {installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("kylfrys", () =>
                  api.andraPlats(id, {
                    kylFrys: {
                      kylMaxGrader: null,
                      kylMinuter: null,
                      frysMaxGrader: null,
                      frysMinuter: null,
                    },
                  }),
                )
              }
            >
              Återställ till förvalen
            </button>
          )}
        </div>
      </form>
    </Kort>
  );
}

/** b13: gränserna för misstänkt is i väderstationen, för alla platser. */
function Vaderstationsdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const g = props.s.vaderstation;
  const [temp, setTemp] = useState(visaTal(g?.isTempGrader.varde ?? 2));
  const [timmar, setTimmar] = useState(visaTal(g?.isTimmar.varde ?? 3));
  const [prognos, setPrognos] = useState(visaTal(g?.isPrognosMs.varde ?? 3));
  if (!g) return null;
  const id = props.s.plats.id;
  const installd = [g.isTempGrader, g.isTimmar, g.isPrognosMs].some((x) => x.installd);

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("vaderstation", async () => {
      const v = [temp, timmar, prognos].map(lasTal);
      if (!v.every(Number.isFinite)) throw new Felsvar("Skriv gränserna som tal.", 400);
      const [isTempGrader, isTimmar, isPrognosMs] = v as number[];
      return api.andraPlats(id, { vaderstation: { isTempGrader, isTimmar, isPrognosMs } });
    });
  }

  const falt = (etikett: string, varde: string, satt: (v: string) => void) => (
    <label>
      {etikett}
      <input
        inputMode="decimal"
        autoComplete="off"
        value={varde}
        onInput={(e) => satt(e.currentTarget.value)}
      />
    </label>
  );

  return (
    <Kort rubrik="Väderstationen">
      <form class="formular" onSubmit={spara}>
        {falt("Misstänkt is under (°C)", temp, setTemp)}
        {falt("i så här många timmar", timmar, setTimmar)}
        {falt("medan prognosen visar minst (m/s)", prognos, setPrognos)}
        <p class="liten">
          Stationen har ingen värme. Står vinden på 0, eller ändras riktningen inte alls, så här
          länge medan det är kallt och prognosen visar vind, är vindvärdena misstänkt is. Då visas
          prognosens vind på kortet Vädret, och värmemodellen använder prognosen. Förval:{" "}
          {visaTal(g.isTempGrader.forval)} °C, {visaTal(g.isTimmar.forval)} timmar och{" "}
          {visaTal(g.isPrognosMs.forval)} m/s{installd ? ". Nu inställda." : "."}
        </p>
        <Beskedrad b={props.besked} sektion="vaderstation" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara väderstationen
          </button>
          {installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("vaderstation", () =>
                  api.andraPlats(id, {
                    vaderstation: { isTempGrader: null, isTimmar: null, isPrognosMs: null },
                  }),
                )
              }
            >
              Återställ till förvalen
            </button>
          )}
        </div>
      </form>
    </Kort>
  );
}

/** b11: gränserna för svartvattentanken, för en båt. */
function Tankdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const g = props.s.tankar;
  const [varning, setVarning] = useState(g ? visaTal(g.svartvattenVarning.varde) : "");
  const [akut, setAkut] = useState(g ? visaTal(g.svartvattenAkut.varde) : "");
  if (!g) return null;
  const id = props.s.plats.id;
  const installd = g.svartvattenVarning.installd || g.svartvattenAkut.installd;

  function spara(e: Event) {
    e.preventDefault();
    void props.spara("tankar", async () => {
      const v = [varning, akut].map(lasTal);
      if (!v.every(Number.isFinite)) throw new Felsvar("Skriv gränserna i procent.", 400);
      const [svartvattenVarning, svartvattenAkut] = v as number[];
      return api.andraPlats(id, { tankar: { svartvattenVarning, svartvattenAkut } });
    });
  }

  return (
    <Kort rubrik="Tankar">
      <form class="formular" onSubmit={spara}>
        <label>
          Varning när svartvattentanken är fylld till (%)
          <input
            inputMode="decimal"
            autoComplete="off"
            value={varning}
            onInput={(e) => setVarning(e.currentTarget.value)}
          />
        </label>
        <label>
          Akut när svartvattentanken är fylld till (%)
          <input
            inputMode="decimal"
            autoComplete="off"
            value={akut}
            onInput={(e) => setAkut(e.currentTarget.value)}
          />
        </label>
        <p class="liten">
          Nivån kommer från tankgivaren via NMEA 2000. Varningen ska vara under Akut. Förval:{" "}
          {visaTal(g.svartvattenVarning.forval)} och {visaTal(g.svartvattenAkut.forval)} %
          {installd ? ". Nu inställd." : "."}
        </p>
        <Beskedrad b={props.besked} sektion="tankar" />
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara tankarna
          </button>
          {installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("tankar", () =>
                  api.andraPlats(id, {
                    tankar: { svartvattenVarning: null, svartvattenAkut: null },
                  }),
                )
              }
            >
              Återställ till förvalen
            </button>
          )}
        </div>
      </form>
    </Kort>
  );
}

/** b11 (BK31): kortet Badtemperatur på Hälsa, av och på. Förvalet är på. */
/**
 * b15: Kom igång. Guiden kan köras igen, och okända enheter kan delas med e-VAtek (av som förval).
 */
function Komigangdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const id = props.s.plats.id;
  const g = props.s.guide;
  return (
    <Kort rubrik="Kom igång">
      <Rad namn="Guiden">{g.klar === null ? "inte körd än" : `kördes ${datumOchTid(g.klar)}`}</Rad>
      <p class="liten">
        Guiden visar det som navet har hittat, ställer några frågor och föreslår hur platsen ställs
        in. Ingenting ändras förrän du bekräftar förslaget.
      </p>
      <div class="knapprad">
        <a class="knapp-huvud knapplank" href={`#plats/${id}/kom-igang`}>
          {g.klar === null ? "Starta guiden" : "Kör guiden igen"}
        </a>
      </div>
      <label class="kryss">
        <input
          type="checkbox"
          checked={g.delaOkanda}
          onChange={(e) => {
            const pa = (e.target as HTMLInputElement).checked;
            void props.spara(
              "komigang",
              () => api.andraPlats(id, { delaOkanda: pa }),
              pa ? "Okända enheter delas med e-VAtek." : "Okända enheter delas inte.",
            );
          }}
        />
        <span>{DELA_TEXT}</span>
      </label>
      <Beskedrad b={props.besked} sektion="komigang" />
    </Kort>
  );
}

function Badtemperaturdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const pa = props.s.badtemperatur !== false;
  const id = props.s.plats.id;
  const bat = props.s.plats.typ === "bat";
  return (
    <Kort rubrik="Badtemperatur">
      <Rad namn="Kortet på Hälsa">{pa ? "visas" : "visas inte"}</Rad>
      <p class="liten">
        Badtemperaturen kommer från den första källa som finns:{" "}
        {bat
          ? "båtens givare i vattnet"
          : "platsens egen givare (en givare i Home Assistant kopplad till vattnets temperatur)"}
        , en uppmätt station inom 30 km (SMHI eller Sjöfartsverket), eller ett beräknat värde från
        Copernicus Marine, som är märkt "beräknad, inte uppmätt". Finns ingen, visas inget kort.
      </p>
      <Beskedrad b={props.besked} sektion="badtemperatur" />
      <div class="knapprad">
        <button
          type="button"
          class="knapp-andra"
          onClick={() =>
            props.spara(
              "badtemperatur",
              () => api.andraPlats(id, { badtemperatur: !pa }),
              pa ? "Kortet Badtemperatur visas inte." : "Kortet Badtemperatur visas.",
            )
          }
        >
          {pa ? "Visa inte badtemperaturen" : "Visa badtemperaturen"}
        </button>
      </div>
    </Kort>
  );
}

function Styrningsdel(props: { s: Platsinstallningar; spara: Spara; besked: Besked | null }) {
  const st = props.s.styrning;
  const [fraga, setFraga] = useState(false);
  if (!st) return null;
  const pa = st.lage.molnet;
  const namn = props.s.plats.namn;
  // b10: för en båt gäller styrningen bara vinterläget.
  const bat = props.s.plats.typ === "bat";
  // b11 (BK33): med vinterläget slår navet på laddarna innan styrningen stängs.
  const vinter = bat ? props.s.vinterlage : null;
  const stanger = st.lage.stanger ?? null;
  const senaste = vinter?.stangning?.senaste ?? null;
  const ord = (v: boolean | null) =>
    v === null ? "okänd, navet har inte sagt något" : v ? "på" : "av";

  return (
    <Kort rubrik="Styrningen">
      <Rad namn="I molnet">{pa ? "På" : stanger ? "Stänger: laddarna slås på först" : "Av"}</Rad>
      {stanger !== null && (
        <p class="meddelande">
          Styrningen stängs sedan {datumOchTid(stanger)}: navet slår på laddarna först, och svarar.
          Svarar det inte, går styrningen till av inom 10 minuter, med en Varning.
        </p>
      )}
      {senaste && stanger === null && (
        <p class={senaste.ok ? "liten" : "meddelande"}>
          Senast styrningen stängdes, {datumOchTid(senaste.tid)}:{" "}
          {senaste.ok
            ? "laddarna är på."
            : "Varning: laddaren kan vara avslagen. Se efter i EasyView."}{" "}
          {senaste.text}
        </p>
      )}
      {st.lage.platsen.map((x) => (
        <Rad key={x.mal} namn={`På platsen, ${MALNAMN[x.mal] ?? x.mal}`}>
          {ord(x.pa)}
        </Rad>
      ))}
      <p class="liten">
        {bat
          ? "För båten styr molnet bara vinterläget. Navet skriver bara när alla tre spärrarna är på: styrningen i molnet här, styrningen på platsen och skrivningen på Pi:n."
          : "Båtkoll styr bara när båda är på: styrningen i molnet här, och platsens egen spärr i navets inställningar."}
      </p>
      {!fraga ? (
        <button type="button" class="knapp-andra" onClick={() => setFraga(true)}>
          {pa ? "Slå av styrningen i molnet" : "Slå på styrningen i molnet"}
        </button>
      ) : (
        <div class="bekraftelse" role="dialog" aria-label="Bekräfta styrningen">
          {pa ? (
            <>
              <p class="meddelande">Slå av styrningen i molnet för {namn}?</p>
              <p class="liten">
                {vinter
                  ? "Laddarna slås på först: navet skriver 1 (laddaren på) till Charger i Masscombi och On/Standby i Plus 1250 där värdet inte redan är 1, läser tillbaka och svarar här. Vinterläget slås av, och navet skriver sedan ingenting mer. Svarar navet inte inom 10 minuter går styrningen ändå till av, och appen visar en Varning: Laddaren kan vara avslagen."
                  : bat
                    ? "Navet skriver då ingenting."
                    : "Molnet skickar då inga kommandon. Planerna står kvar, och appen visar vad som skulle ha gjorts."}
              </p>
            </>
          ) : (
            <>
              <p class="meddelande">Slå på styrningen i molnet för {namn}?</p>
              <p class="liten">
                {bat
                  ? "Molnet skickar då vinterlägets kommandon till navet:"
                  : "Molnet skickar då kommandon till navet enligt planerna:"}
              </p>
              <ul class="liten">
                {st.vad.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
              <p class="liten">
                Navet gör ändå ingenting förrän platsens egen spärr också är på, i navets
                inställningar. Den är nu:{" "}
                {st.lage.platsen
                  .map((x) => `${ord(x.pa)} för ${MALNAMN[x.mal] ?? x.mal}`)
                  .join(", ") || "okänd"}
                .
              </p>
            </>
          )}
          <div class="knapprad">
            <button
              type="button"
              class="knapp-huvud"
              onClick={async () => {
                await props.spara(
                  "styrning",
                  () => api.styrning(props.s.plats.id, !pa),
                  !pa
                    ? "Styrningen i molnet är på."
                    : vinter
                      ? "Styrningen i molnet stängs: navet slår på laddarna först. Svaret kommer här inom ett par minuter."
                      : "Styrningen i molnet är av.",
                );
                setFraga(false);
              }}
            >
              {pa ? (vinter ? "Ja, slå på laddarna och slå av" : "Ja, slå av") : "Ja, slå på"}
            </button>
            <button type="button" class="knapp-andra" onClick={() => setFraga(false)}>
              Avbryt
            </button>
          </div>
        </div>
      )}
      {stanger !== null && (
        <div class="knapprad">
          <button
            type="button"
            class="knapp-andra"
            onClick={() =>
              props.spara("styrning", () => api.installningar(props.s.plats.id), "Hämtat.")
            }
          >
            Hämta läget igen
          </button>
        </div>
      )}
      <Beskedrad b={props.besked} sektion="styrning" />
    </Kort>
  );
}

/** Navets logg (b7), de senaste raderna först. Den hämtas när den öppnas. */
function Navlogg(props: { platsId: string; sort: Navsort }) {
  const [rader, setRader] = useState<Navloggrad[] | null>(null);
  const [fler, setFler] = useState(false);
  const [fel, setFel] = useState<string | null>(null);

  async function hamta(fore?: number) {
    setFel(null);
    try {
      const r = await api.navlogg(props.platsId, props.sort, fore);
      setRader((f) => [...(fore ? (f ?? []) : []), ...r.rader]);
      setFler(r.fler);
    } catch {
      setFel("Loggen gick inte att hämta.");
    }
  }

  return (
    <details
      class="avancerat navlogg"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && rader === null) void hamta();
      }}
    >
      <summary>Navets logg</summary>
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
      {rader === null ? (
        <p class="laddar">Hämtar …</p>
      ) : rader.length === 0 ? (
        <p class="meddelande">Inga rader än. Navet skickar loggen från appen 1.0.0.</p>
      ) : (
        <ol class="logg navloggrader">
          {rader.map((r) => (
            <li key={r.id}>
              <time dateTime={new Date(r.tid).toISOString()}>{datumOchTid(r.tid)}</time>{" "}
              <code>{r.rad}</code>
            </li>
          ))}
        </ol>
      )}
      {fler && rader && (
        <button type="button" class="knapp-andra" onClick={() => void hamta(rader.at(-1)?.id)}>
          Hämta fler
        </button>
      )}
    </details>
  );
}

/** b20-2-1: hur länge sedan i ord utan "för … sedan", till exempel "12 min", "3 tim" eller "2 dygn". */
export function langdSedan(ms: number, nu: number): string {
  const min = Math.max(0, Math.round((nu - ms) / 60_000));
  if (min < 1) return "mindre än en minut";
  if (min < 60) return `${min} min`;
  const tim = Math.round(min / 60);
  if (tim < 48) return `${tim} tim`;
  return `${Math.round(tim / 24)} dygn`;
}

/** Navet i en mening efter "Navet": "ombord", "i huset (Nobø)" eller "Home Assistant". */
const NAVSORT_I_TEXT: Record<Navsort, string> = {
  ombord: "ombord",
  hus: "i huset (Nobø)",
  ha: "Home Assistant",
};

/** Navet har hörts av de senaste 24 timmarna. */
const NYLIGEN_MS = 24 * 3_600_000;

/**
 * b20-2-1: frågan innan appen byter navets nyckel. Ett nav som hörts av de senaste 24 timmarna
 * slutar fungera när nyckeln byts, så då står det hur länge sedan det hördes av. Har navet aldrig
 * hörts av, eller inte på över 24 timmar, räcker en enklare fråga. Avbryt är förvald.
 */
/**
 * b20-2-2: Avbryt får fokus i samma ögonblick som knappen skapas, inte efter målningen (useEffect).
 * Med useEffect hann ett Enter som kom direkt efter klicket träffa knappen "Ny nyckel för …" i
 * stället, och frågan låg kvar. Funktionen är densamma varje gång (en stabil ref), så fokus
 * flyttas bara när frågan öppnas och inte när sidan ritas om, till exempel av klockan.
 */
const fokusera = (knapp: HTMLButtonElement | null) => knapp?.focus();

function Nyckelfraga(props: {
  sort: Navsort;
  senastHord: number | null;
  nu: number;
  byt: () => void;
  avbryt: () => void;
}) {
  const nyligen = props.senastHord !== null && props.nu - props.senastHord <= NYLIGEN_MS;
  const sort = NAVSORT_I_TEXT[props.sort];
  return (
    <div
      class="bekraftelse"
      role="dialog"
      aria-label="Bekräfta ny nyckel"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          props.avbryt();
        }
      }}
    >
      {nyligen && props.senastHord !== null ? (
        <p class="meddelande">
          Navet {sort} hördes av för {langdSedan(props.senastHord, props.nu)} sedan. En ny nyckel
          gör att det slutar fungera tills den nya nyckeln är inlagd på navet. Byta nyckel?
        </p>
      ) : (
        <p class="meddelande">
          Navet {sort}{" "}
          {props.senastHord === null
            ? "har inte hörts av"
            : "hördes senast av för över ett dygn sedan"}
          . Byta nyckel? Den gamla nyckeln slutar gälla direkt.
        </p>
      )}
      <div class="knapprad">
        <button ref={fokusera} type="button" class="knapp-huvud" autofocus onClick={props.avbryt}>
          Avbryt
        </button>
        <button type="button" class="knapp-andra" onClick={props.byt}>
          Byt nyckel
        </button>
      </div>
    </div>
  );
}

function Navdel(props: {
  s: Platsinstallningar;
  nu: number;
  nyNyckel: (sort: Navsort) => Promise<void>;
  besked: Besked | null;
}) {
  const [fraga, setFraga] = useState<Navsort | null>(null);
  const finns = (sort: Navsort) => props.s.nav.some((n) => n.sort === sort);
  return (
    <Kort rubrik="Nav" bred>
      {props.s.nav.length === 0 && <p class="meddelande">Inget nav än.</p>}
      {props.s.nav.map((n) => (
        <div key={n.sort} class="navet">
          <Rad namn={NAVNAMN[n.sort]}>
            {n.senastHord === null
              ? "har inte hörts av"
              : `hördes av ${sedan(n.senastHord, props.nu)}`}
          </Rad>
          <Rad namn="Version">
            {n.version
              ? `${n.version}${n.prov ? " (provas)" : ""}, startdelen ${n.startdel ?? "okänd"}`
              : "äldre än appen 1.0.0: uppdateras inte från molnet"}
          </Rad>
          {n.uppdaterad !== null && <Rad namn="Uppdaterad">{datumOchTid(n.uppdaterad)}</Rad>}
          {n.tillbakagang && (
            <p class="liten varning-text">
              Gick tillbaka från {n.tillbakagang.fran} till {n.tillbakagang.till}{" "}
              {datumOchTid(n.tillbakagang.tid)}: {n.tillbakagang.orsak}.
            </p>
          )}
          <Navlogg platsId={props.s.plats.id} sort={n.sort} />
        </div>
      ))}
      {props.s.navVersion && (
        <p class="liten">Platsen är låst vid navets version {props.s.navVersion}.</p>
      )}
      <Rad namn="Golvet">{props.s.navGolv}</Rad>
      <p class="liten">
        Inget paket under golvet körs, och molnet erbjuder inget. Golvet höjs när ett säkerhetsfel
        rättas. Appen 1.0.0 i Home Assistant känner inte till golvet: det gäller från appen 1.1.0.
      </p>
      <div class="knapprad">
        {props.s.navsorter.map((sort) =>
          finns(sort) ? (
            <button key={sort} type="button" class="knapp-andra" onClick={() => setFraga(sort)}>
              Ny nyckel för {NAVET[sort]}
            </button>
          ) : (
            <button
              key={sort}
              type="button"
              class="knapp-andra"
              onClick={() => props.nyNyckel(sort)}
            >
              Lägg till {NAVET[sort]}
            </button>
          ),
        )}
      </div>
      {fraga && (
        <Nyckelfraga
          sort={fraga}
          senastHord={props.s.nav.find((n) => n.sort === fraga)?.senastHord ?? null}
          nu={props.nu}
          avbryt={() => setFraga(null)}
          byt={async () => {
            const sort = fraga;
            setFraga(null);
            await props.nyNyckel(sort);
          }}
        />
      )}
      <Beskedrad b={props.besked} sektion="nav" />
    </Kort>
  );
}

function Kontodel(props: {
  s: Platsinstallningar;
  spara: Spara;
  besked: Besked | null;
  borta: () => void;
}) {
  const [epost, setEpost] = useState("");
  const [roll, setRoll] = useState<Roll>("lasare");
  const [fraga, setFraga] = useState<string | null>(null);
  const id = props.s.plats.id;
  const agare = props.s.konton.filter((k) => k.roll === "agare").length;

  function bjud(e: Event) {
    e.preventDefault();
    void props.spara("konton", async () => {
      const r = await api.bjudIn(id, epost, roll);
      return [r.installningar, r.meddelande];
    });
  }

  return (
    <Kort rubrik="Ägare och läsare" bred>
      <ul class="konton">
        {props.s.konton.map((k) => (
          <li key={k.id}>
            <span>
              {k.epost}
              {k.du ? " (du)" : ""} · {k.roll === "agare" ? "ägare" : "läsare"}
            </span>
            {k.roll === "agare" && agare <= 1 ? (
              <span class="liten">Den sista ägaren kan inte tas bort.</span>
            ) : fraga === k.id ? (
              <span class="knapprad">
                <button
                  type="button"
                  class="knapp-huvud"
                  onClick={async () => {
                    setFraga(null);
                    const ok = await props.spara(
                      "konton",
                      async () => {
                        const r = await api.taBortKonto(id, k.id);
                        // Den som tog bort sig själv får inga nya inställningar.
                        return "borta" in r ? props.s : r;
                      },
                      `${k.epost} är borttagen.`,
                    );
                    if (ok && k.du) props.borta();
                  }}
                >
                  Ja, ta bort {k.du ? "mig" : k.epost}
                </button>
                <button type="button" class="knapp-andra" onClick={() => setFraga(null)}>
                  Avbryt
                </button>
              </span>
            ) : (
              <button type="button" class="knapp-lank" onClick={() => setFraga(k.id)}>
                Ta bort
              </button>
            )}
          </li>
        ))}
      </ul>
      <form class="formular" onSubmit={bjud}>
        <label>
          Bjud in med e-post
          <input
            type="email"
            required
            autoComplete="off"
            value={epost}
            onInput={(e) => setEpost(e.currentTarget.value)}
          />
        </label>
        <label>
          Som
          <select value={roll} onChange={(e) => setRoll(e.currentTarget.value as Roll)}>
            <option value="lasare">Läsare: ser platsen och får notiserna</option>
            <option value="agare">Ägare: planerar och ändrar också inställningarna</option>
          </select>
        </label>
        <p class="liten">
          Har personen inget konto skapas det. Inbjudan kommer med e-post från Båtkoll, och personen
          loggar in med sin e-post.
        </p>
        <Beskedrad b={props.besked} sektion="konton" />
        <button class="knapp-huvud" type="submit">
          Bjud in
        </button>
      </form>
    </Kort>
  );
}

export function Installningsvy(props: {
  platsId: string;
  nu: number;
  /** Platsen ändrades: namnet och typen i listorna. */
  andrat: () => Promise<void>;
  /** Kontot har inte längre platsen. */
  borta: () => void;
}) {
  const [s, setS] = useState<Platsinstallningar | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [besked, setBesked] = useState<Besked | null>(null);
  const [nyckel, setNyckel] = useState<{ nyckel: string; sort: Navsort } | null>(null);
  // Ny version efter varje sparning, så att formulären visar det som sparades.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    setS(null);
    setFel(null);
    setNyckel(null);
    api
      .installningar(props.platsId)
      .then((r) => {
        setS(r);
        setVersion((v) => v + 1);
      })
      .catch((e) =>
        setFel(
          e instanceof Felsvar && e.status === 403
            ? "Bara ägaren ser och ändrar platsens inställningar."
            : "Inställningarna gick inte att hämta.",
        ),
      );
  }, [props.platsId]);

  const spara: Spara = async (sektion, gor, ok = "Sparat.") => {
    setBesked({ sektion, text: null, fel: null });
    try {
      const r = await gor();
      const [ny, text] = Array.isArray(r) ? r : [r, ok];
      setS(ny);
      setVersion((v) => v + 1);
      setBesked({ sektion, text, fel: null });
      await props.andrat();
      return true;
    } catch (x) {
      setBesked({
        sektion,
        text: null,
        fel: x instanceof Felsvar ? x.message : "Det gick inte att spara.",
      });
      return false;
    }
  };

  async function nyNyckel(sort: Navsort) {
    setBesked({ sektion: "nav", text: null, fel: null });
    try {
      setNyckel(await api.nyttNav(props.platsId, sort));
      setS(await api.installningar(props.platsId));
      setBesked(null);
    } catch (x) {
      setBesked({
        sektion: "nav",
        text: null,
        fel: x instanceof Felsvar ? x.message : "Nyckeln gick inte att ta fram.",
      });
    }
  }

  if (fel) return <p class="tomt">{fel}</p>;
  if (!s) return <p class="laddar">Hämtar …</p>;
  const del = { s, spara, besked };
  return (
    <div class="rutnat installningar">
      <h2 class="kort-bred">Inställningar</h2>
      {nyckel && (
        <Nyckelruta nyckel={nyckel.nyckel} sort={nyckel.sort} klar={() => setNyckel(null)} />
      )}
      <Komigangdel {...del} />
      <Platsdel key={`plats${version}`} {...del} />
      <Driftdel {...del} />
      <Eldel key={`el${version}`} {...del} />
      <Forvarmningsdel key={`forvarmning${version}`} {...del} />
      {s.plats.typ !== "bat" && (
        <Varmeprofildel
          key={`varmeprofil${version}`}
          plats={s.plats.id}
          // Loggen över ändringarna hämtas på nytt, utan att formulären byts ut.
          sparad={async () => setS(await api.installningar(props.platsId))}
        />
      )}
      <Vinddel key={`vind${version}`} {...del} />
      <KylFrysdel key={`kylfrys${version}`} {...del} />
      <Tankdel key={`tankar${version}`} {...del} />
      <Badtemperaturdel {...del} />
      <Vaderstationsdel key={`vaderstation${version}`} {...del} />
      <Ytterdorrdel
        key={`ytterdorr${version}`}
        s={s}
        nu={props.nu}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="ytterdorr" />}
      />
      <Batdel
        key={`motorer${version}`}
        s={s}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="motorer" />}
      />
      <Jordgivardel
        key={`jordgivare${version}`}
        s={s}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="jordgivare" />}
      />
      <Victrondel
        key={`victron${version}`}
        s={s}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="victron" />}
      />
      <Homeydel
        key={`homey${version}`}
        s={s}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="homey" />}
      />
      <Husforbrukardel
        key={`husforbrukare${version}`}
        s={s}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="husforbrukare" />}
      />
      <Givardel
        key={`givare${version}`}
        s={s}
        nu={props.nu}
        spara={(sektion, gor) => spara(sektion, gor)}
        beskedrad={<Beskedrad b={besked} sektion="givare" />}
      />
      <Vinterdel
        key={`vinter${version}`}
        s={s}
        nu={props.nu}
        spara={(sektion, gor, ok) => spara(sektion, gor, ok)}
        beskedrad={<Beskedrad b={besked} sektion="vinter" />}
      />
      <Styrningsdel key={`styrning${version}`} {...del} />
      <Navdel s={s} nu={props.nu} nyNyckel={nyNyckel} besked={besked} />
      <Avanceratdel key={`avancerat${version}`} {...del} />
      <Kontodel key={`konton${version}`} {...del} borta={props.borta} />
      <Kort rubrik="Loggen" bred>
        {s.logg.length === 0 ? (
          <p class="meddelande">Inga ändringar än.</p>
        ) : (
          <ol class="logg">
            {s.logg.map((r) => (
              <li key={`${r.tid}${r.text}`}>
                <time dateTime={new Date(r.tid).toISOString()}>{datumOchTid(r.tid)}</time>{" "}
                <span class="vem">{r.vem}</span>: {r.text}
              </li>
            ))}
          </ol>
        )}
      </Kort>
    </div>
  );
}
